import { useState, useMemo } from 'react';
import { ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { toolbarControl } from '@/lib/buttonStyles';
import { Input } from '@/components/ui/input';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { GRADING_COMPANIES, findGradingCompany } from '@/constants/gradingCompanies';

interface GradingCompanyComboboxProps {
  value: string;
  onChange: (value: string) => void;
  customName?: string;
  onCustomNameChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** When provided, only these company `value`s appear in the list. */
  allowedCompanies?: string[];
  /** Visual variant. `toolbar` matches other filter-tray dropdowns (Status/Language). */
  variant?: 'default' | 'toolbar';
}

export function GradingCompanyCombobox({
  value,
  onChange,
  customName = '',
  onCustomNameChange,
  placeholder = 'Select grading company...',
  className,
  allowedCompanies,
  variant = 'default',
}: GradingCompanyComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const selectedCompany = useMemo(() => {
    return GRADING_COMPANIES.find(c => c.value === value);
  }, [value]);

  const scopedCompanies = useMemo(() => {
    if (!allowedCompanies) return GRADING_COMPANIES;
    return GRADING_COMPANIES.filter((c) => allowedCompanies.includes(c.value));
  }, [allowedCompanies]);

  const filteredCompanies = useMemo(() => {
    if (!searchQuery) return scopedCompanies;
    const matched = findGradingCompany(searchQuery);
    if (!allowedCompanies) return matched;
    return matched.filter((c) => allowedCompanies.includes(c.value));
  }, [searchQuery, scopedCompanies, allowedCompanies]);

  // Display "Other" capitalized when selected, otherwise show the company name
  const getDisplayValue = () => {
    if (value === 'other') return 'Other';
    if (selectedCompany) return selectedCompany.shortName;
    if (value) return value; // custom value typed in search
    return placeholder;
  };

  const displayValue = getDisplayValue();
  const isOtherSelected = value === 'other';

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={variant === 'toolbar' ? 'outline' : 'ghost'}
            role="combobox"
            aria-expanded={open}
            className={cn(
              variant === 'toolbar'
                ? cn(
                    'w-full justify-between h-9 !bg-[hsl(var(--toolbar-control-bg,var(--background)))]',
                    toolbarControl,
                  )
                : cn(
                    'w-full justify-between font-normal rounded-md h-14 field-surface field-border hover:bg-white/[0.03] px-3',
                    !value && 'text-muted-foreground',
                  ),
              className,
            )}
          >
            <span className="truncate">{displayValue}</span>
            <ChevronsUpDown className={cn(
              'ml-2 h-4 w-4 shrink-0 opacity-50 transition-colors',
              variant === 'toolbar' && open && 'opacity-100 text-foreground',
            )} />
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-[var(--radix-popover-trigger-width)] p-0 bg-popover border-border z-50" 
          align="start"
          side="bottom"
          sideOffset={4}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
        >
          <Command shouldFilter={false}>
            <CommandInput 
              placeholder="Search grading companies..." 
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList className="max-h-[200px] overflow-y-auto overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch]">
              <CommandEmpty>
                {searchQuery ? (
                  <CommandItem
                    value={searchQuery}
                    onSelect={() => {
                      onChange(searchQuery);
                      setOpen(false);
                      setSearchQuery('');
                    }}
                    className="cursor-pointer"
                  >
                    Use "{searchQuery}" as custom company
                  </CommandItem>
                ) : (
                  'No grading company found.'
                )}
              </CommandEmpty>
              <CommandGroup>
                {filteredCompanies.map((company) => (
                  <CommandItem
                    key={company.value}
                    value={company.value}
                    onSelect={() => {
                      onChange(company.value);
                      setOpen(false);
                      setSearchQuery('');
                    }}
                    className="cursor-pointer"
                  >
                    <span className="font-medium">{company.shortName}</span>
                    <span className="ml-2 text-muted-foreground text-sm">
                      {company.label.replace(`${company.shortName} `, '').replace('(', '').replace(')', '')}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandGroup className="border-t border-white/10">
                <CommandItem
                  value="other"
                  onSelect={() => {
                    onChange('other');
                    setOpen(false);
                    setSearchQuery('');
                  }}
                  className="cursor-pointer"
                >
                  <span className="font-medium">Other</span>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {isOtherSelected && onCustomNameChange && (
        <div className="space-y-1.5 pt-1">
          <label className="block text-[11px] font-medium uppercase tracking-wide text-muted-foreground/80 px-0.5">
            Custom Company Name
          </label>
          <Input
            placeholder="e.g. PSA Japan, ARS, CCIC…"
            value={customName}
            onChange={(e) => onCustomNameChange(e.target.value)}
            className="w-full h-12 field-surface field-border placeholder:text-muted-foreground/50"
            maxLength={60}
            autoFocus
          />
        </div>
      )}
    </>
  );
}
